import { GRID, type ViewRules } from '../engine';

export type UaClass = 'kakao' | 'naver' | 'meta' | 'line' | 'chrome' | 'safari' | 'other';
export type Os = 'android' | 'ios' | 'other';

export interface SafeInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface ViewportProbe {
  readonly vpW: number;
  readonly vpH: number;
  readonly physW: number;
  readonly physH: number;
  readonly dpr: number;
  readonly safe: SafeInsets;
  /** 회전 안내 대상(손에 든 터치 기기) — isHandheldTouch */
  readonly isTouch: boolean;
  readonly uaClass: UaClass;
  readonly os: Os;
  readonly bottomGuard: number;
  readonly orientation: 'portrait' | 'landscape';
}

export interface LayoutMetrics {
  readonly cell: number; // device px
  readonly outer: number; // device px
  readonly canvasDeviceW: number;
  readonly canvasDeviceH: number;
  readonly canvasCssW: number;
  readonly canvasCssH: number;
  readonly bandCss: number;
  readonly hudCss: number;
  readonly marginTopCss: number;
  readonly glyphDevice: number;
  readonly tier: 'normal' | 'floor';
  readonly tooSmall: boolean;
  readonly degraded: boolean;
}

const IN_APP_PATTERNS: readonly (readonly [UaClass, RegExp])[] = [
  ['kakao', /KAKAOTALK/i],
  ['naver', /NAVER\(inapp/i],
  ['meta', /FBAN|FBAV|Instagram/i],
  ['line', /\bLine\//i],
];
const IN_APP_UA = new Set<UaClass>(['kakao', 'naver', 'meta', 'line']);

export function classifyUa(ua: string): UaClass {
  for (const [c, re] of IN_APP_PATTERNS) if (re.test(ua)) return c;
  if (/CriOS|Chrome/i.test(ua)) return 'chrome';
  if (/Safari/i.test(ua)) return 'safari';
  return 'other';
}

export function classifyOs(ua: string, maxTouchPoints: number): Os {
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Macintosh/.test(ua) && maxTouchPoints > 1) return 'ios';
  return 'other';
}

function readSafeInsets(): SafeInsets {
  const el = document.getElementById('safe-probe');
  if (el === null) return { top: 0, right: 0, bottom: 0, left: 0 };
  const cs = getComputedStyle(el);
  const n = (v: string): number => {
    const f = Number.parseFloat(v);
    return Number.isFinite(f) ? f : 0;
  };
  return {
    top: n(cs.paddingTop),
    right: n(cs.paddingRight),
    bottom: n(cs.paddingBottom),
    left: n(cs.paddingLeft),
  };
}

export interface PointerMedia {
  /** `(pointer: …)` 미디어 쿼리를 아는 브라우저인가 */
  readonly pointerQuery: boolean;
  readonly coarse: boolean;
  readonly hover: boolean;
  readonly maxTouchPoints: number;
}

/**
 * 회전 안내 대상 — 손에 든 터치 기기(RULES §2.7). 주 포인터가 coarse 이고 hover 가 없을 때만 참.
 * `maxTouchPoints > 0` 만으로 판정하면 터치스크린 노트북(주 포인터는 트랙패드)이 가로 화면에서 영구히 막힌다.
 * 포인터 미디어 쿼리를 모르는 구형 WebView 만 maxTouchPoints 로 판정한다.
 */
export function isHandheldTouch(m: PointerMedia): boolean {
  return m.pointerQuery ? m.coarse && !m.hover : m.maxTouchPoints > 0;
}

export function probeViewport(R: ViewRules): ViewportProbe {
  const vv = window.visualViewport;
  const rawW = Math.min(
    vv?.width ?? Number.POSITIVE_INFINITY,
    document.documentElement.clientWidth,
    window.innerWidth,
  );
  const rawH = Math.min(
    vv?.height ?? Number.POSITIVE_INFINITY,
    document.documentElement.clientHeight,
    window.innerHeight,
  );
  const safe = readSafeInsets();
  const ua = navigator.userAgent;
  const uaClass = classifyUa(ua);
  const os = classifyOs(ua, navigator.maxTouchPoints);
  const mq = (q: string): boolean => window.matchMedia(q).matches;
  const isTouch = isHandheldTouch({
    pointerQuery: mq('(pointer: coarse), (pointer: fine), (pointer: none)'),
    coarse: mq('(pointer: coarse)'),
    hover: mq('(hover: hover)'),
    maxTouchPoints: navigator.maxTouchPoints,
  });
  const bottomGuard = IN_APP_UA.has(uaClass) && os === 'android' ? R.inAppBottomGuardCss : 0;
  const dpr = window.devicePixelRatio || 1;
  const vpW = Math.max(0, rawW - safe.left - safe.right);
  const vpH = Math.max(0, rawH - safe.top - safe.bottom);
  return {
    vpW,
    vpH,
    physW: Math.round(vpW * dpr),
    physH: Math.round(vpH * dpr),
    dpr,
    safe,
    isTouch,
    uaClass,
    os,
    bottomGuard,
    orientation: vpW > vpH ? 'landscape' : 'portrait',
  };
}

const EPS = 1e-6;

/** device px 정수 격자. 남는 픽셀은 필드 좌우 레터박스로 버린다. */
export function computeLayout(p: ViewportProbe, R: ViewRules): LayoutMetrics {
  const FRAME_D = Math.round(6 * p.dpr);
  const SHAKE_D = Math.round(4 * p.dpr);
  const OUTER_D = FRAME_D + SHAKE_D;
  const cellMax = Math.min(96, Math.round(R.cellCssMax * p.dpr));

  const cellFor = (bandMin: number, hudMin: number): number => {
    const availW = p.physW - 2 * OUTER_D;
    const availH = p.physH - Math.round((bandMin + hudMin) * p.dpr) - 2 * OUTER_D;
    const byW = availW / GRID.COLS;
    const byH = availH / GRID.ROWS;
    return Math.max(1, Math.min(Math.floor(Math.min(byW, byH) + EPS), cellMax));
  };

  let tier: 'normal' | 'floor' = 'normal';
  let cell = cellFor(R.bandMinCss, R.hudMinCss);
  if (cell / p.dpr < R.cellCssMin) {
    const c2 = cellFor(R.bandFloorCss, R.hudFloorCss);
    if (c2 > cell) {
      cell = c2;
      tier = 'floor';
    }
  }
  const bandBase = tier === 'floor' ? R.bandFloorCss : R.bandMinCss;
  const hudBase = tier === 'floor' ? R.hudFloorCss : R.hudMinCss;

  let canvasCssH = 0;
  let bandH = bandBase;
  let hudH = hudBase;
  let mTop = 0;
  let tooSmall = false;
  for (;;) {
    canvasCssH = (cell * GRID.ROWS + 2 * OUTER_D) / p.dpr;
    const slack = Math.max(0, p.vpH - canvasCssH - bandBase - hudBase);
    const usable = Math.max(0, slack - p.bottomGuard);
    bandH = bandBase + Math.min(R.bandMaxCss - bandBase, Math.floor(usable * R.slackToBand));
    hudH = hudBase + Math.min(R.hudMaxCss - hudBase, Math.floor(usable * R.slackToHud));
    const rest = p.vpH - canvasCssH - bandH - hudH;
    if (rest >= p.bottomGuard) {
      mTop = rest - Math.max(p.bottomGuard, Math.floor(rest * (1 - R.marginTopRatio)));
      break;
    }
    if (cell <= 8) {
      tooSmall = true;
      mTop = 0;
      break;
    }
    cell -= 1;
  }
  if (cell / p.dpr < 12) tooSmall = true;

  const canvasDeviceW = cell * GRID.COLS + 2 * OUTER_D;
  const canvasDeviceH = cell * GRID.ROWS + 2 * OUTER_D;
  const glyphDevice = Math.max(16, Math.floor((cell * R.tileFill) / 16) * 16);

  return {
    cell,
    outer: OUTER_D,
    canvasDeviceW,
    canvasDeviceH,
    canvasCssW: canvasDeviceW / p.dpr,
    canvasCssH: canvasDeviceH / p.dpr,
    bandCss: bandH,
    hudCss: hudH,
    marginTopCss: Math.max(0, mTop),
    glyphDevice,
    tier,
    tooSmall,
    degraded: tooSmall || glyphDevice < 16 || cell / p.dpr < R.cellCssMin,
  };
}

export function applyLayout(root: HTMLElement, canvas: HTMLCanvasElement, m: LayoutMetrics): void {
  canvas.width = m.canvasDeviceW;
  canvas.height = m.canvasDeviceH;
  canvas.style.width = `${m.canvasCssW}px`;
  canvas.style.height = `${m.canvasCssH}px`;
  root.style.setProperty('--band-h', `${m.bandCss}px`);
  root.style.setProperty('--hud-h', `${m.hudCss}px`);
  root.style.setProperty('--margin-top', `${m.marginTopCss}px`);
}
